/**
 * Shared financial helper utilities.
 * Extracted from FinancialDashboard, InvoiceListPage, InvoiceDetailPage, PlatformFeeConfigPage.
 */

/**
 * Format a number as Brazilian Real currency (R$ X.XXX,XX).
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Conversão de valores monetários entre a unidade de ARMAZENAMENTO (inteiro em
 * centavos, ex.: R$ 3,30 => 330) e a unidade da APLICAÇÃO/formulários (reais,
 * ex.: 3.3). Toda conversão deve passar por estes helpers para que carregar
 * (load) e salvar (save) sejam sempre simétricos e nunca divirjam.
 */

/**
 * Converte centavos (armazenamento) para reais (formulário).
 * Retorna undefined para null/undefined, preservando campos opcionais vazios.
 */
export function centsToReais(cents: number | null | undefined): number | undefined {
  return cents == null ? undefined : cents / 100;
}

/**
 * Converte reais (formulário) para centavos (armazenamento).
 * Usa Math.round para evitar erros de ponto flutuante (ex.: 19.99 * 100 =
 * 1998.9999999999998, que sem arredondamento seria truncado para 1998).
 * Retorna undefined para null/undefined, preservando campos opcionais vazios.
 */
export function reaisToCents(reais: number | null | undefined): number | undefined {
  return reais == null ? undefined : Math.round(reais * 100);
}

/**
 * Return a list of months with labels in Portuguese.
 */
export function getAvailableMonths(): { value: number; label: string }[] {
  return [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];
}

/**
 * Return an array of years from (currentYear + 1) down to (currentYear - 3).
 */
export function getAvailableYears(): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear + 1; y >= currentYear - 3; y--) {
    years.push(y);
  }
  return years;
}

// ============================================================================
// INVOICE DISPLAY CONSTANTS
// ============================================================================

export const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Aberta',
  CLOSED: 'Fechada',
  PAID: 'Paga',
  CANCELLED: 'Cancelada',
};

export const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'error'> = {
  OPEN: 'warning',
  CLOSED: 'info',
  PAID: 'success',
  CANCELLED: 'error',
};

export const TYPE_LABELS: Record<string, string> = {
  SERVICE: 'Serviço',
  PLATFORM: 'Plataforma',
};

export const TYPE_COLORS: Record<string, 'primary' | 'secondary'> = {
  SERVICE: 'primary',
  PLATFORM: 'secondary',
};

/**
 * Format a reference period as MM/YYYY.
 */
export function formatPeriod(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${year}`;
}
